/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { htmlSafe } from '@ember/template';
import { SafeString } from 'handlebars';
import { DEFAULT_CHART_OPTIONS } from 'cio/utils/graphs';
import merge from 'lodash.merge';
import { Periods, PERIOD_SERIES_LENGTHS } from 'cio/utils/metrics/common';
import moment from 'moment-timezone';
import { inject as service } from '@ember/service';
import HighchartsService from 'cio/services/highcharts';
import Ember from 'ember';
import type { Chart, HTMLDOMElement, Options, XAxisOptions, SVGElement, Series } from 'highcharts';

const EASTERN_TIME_ZONE = 'Etc/GMT+4';
const ANNOTATION_TRIGGER_HEIGHT = 16;
const ANNOTATION_TRIGGER_WIDTH = 14;
const ANNOTATION_TOOLTIP_HEIGHT = 135;
const ANNOTATION_TOOLTIP_WIDTH = 300;
const ANNOTATION_HOVER_CONTAINER_SIZE = 30;

enum TooltipClassModifier {
  Top = 'top',
  TopLeft = 'top-left',
  TopRight = 'top-right',
}

interface HcBaseChartArgs {
  chartOptions: Options;
  period?: Periods;
  xAxisDateTimePeriod?: Periods;
  timeRange?: { start?: moment.Moment; end?: moment.Moment } | null;
  timezone?: string;
  loading?: boolean;
  loadingNotice?: string;
  preserveAspectRatio?: string;
  showUntrackedChangeAnnotation?: boolean;
  enableExport?: boolean;
}

export default class HcBaseChart extends Component<HcBaseChartArgs> {
  @service highcharts!: HighchartsService;

  @tracked pinTooltip: SVGElement | null = null;

  chartContainer: HTMLElement | null = null;
  chart: Chart | null = null;
  pin: SVGElement | null = null;
  pinHoverRect: SVGElement | null = null;

  get showUntrackedChangeAnnotation() {
    return this.args.showUntrackedChangeAnnotation ?? false;
  }

  get timezone() {
    return this.args.timezone ?? this.args.chartOptions?.time?.timezone ?? EASTERN_TIME_ZONE;
  }

  get xAxisMin() {
    if (this.args.period === Periods.CUSTOM && this.args.timeRange?.start) {
      return moment.tz(`${this.args.timeRange.start.format('YYYY-MM-DD')} 00:00:00`, this.timezone).unix() * 1000;
    } else if (this.args.period === Periods.DAYS) {
      // We subtract 1 from DAYS series length because we want a total of
      // 30 (for the days period) data points, including today. So we
      // subtract 29 days to get the right timestamp.
      return (
        moment
          .tz(this.timezone)
          .add(-(PERIOD_SERIES_LENGTHS[Periods.DAYS] - 1), 'days')
          .startOf('day')
          .unix() * 1000
      );
    } else if (this.args.period === Periods.WEEKS) {
      let weeksPeriodMin = moment.tz(this.timezone).startOf('day');

      if (this.timezone === EASTERN_TIME_ZONE) {
        // Weekly calculations in the backend assume weeks start on Sundays. Moment
        // only supports isoWeek, which always starts on Mondays, or week, which is
        // locale-dependent. We need to make sure to calculate weeks based on
        // Sundays in this place, so we find it ourselves.
        // This applies to delivery metrics, which are based in UTC-4 time. If
        // another timezone is specified, we'll assume weeks start on Mondays.
        weeksPeriodMin = weeksPeriodMin.subtract(weeksPeriodMin.day(), 'days');
      } else {
        weeksPeriodMin = weeksPeriodMin.startOf('isoWeek');
      }

      return weeksPeriodMin.add(-(PERIOD_SERIES_LENGTHS[Periods.WEEKS] - 1), 'weeks').unix() * 1000;
    } else if (this.args.period === Periods.MONTHS) {
      return (
        moment
          .tz(this.timezone)
          .add(-(PERIOD_SERIES_LENGTHS[Periods.MONTHS] - 1), 'months')
          .startOf('month')
          .unix() * 1000
      );
    }

    return null;
  }

  get xAxisMax() {
    if (this.args.period === Periods.CUSTOM && this.args.timeRange?.end) {
      return moment.tz(`${this.args.timeRange.end.format('YYYY-MM-DD')} 00:00:00`, this.timezone).unix() * 1000;
    } else if (this.args.period === Periods.DAYS) {
      return moment.tz(this.timezone).startOf('day').unix() * 1000;
    } else if (this.args.period === Periods.WEEKS) {
      let weeksPeriodMin = moment.tz(this.timezone).startOf('day');

      if (this.timezone === EASTERN_TIME_ZONE) {
        // Weekly calculations in the backend assume weeks start on Sundays. Moment
        // only supports isoWeek, which always starts on Mondays, or week, which is
        // locale-dependent. We need to make sure to calculate weeks based on
        // Sundays in this place, so we find it ourselves.
        // This applies to delivery metrics, which are based in UTC-4 time. If
        // another timezone is specified, we'll assume weeks start on Mondays.
        weeksPeriodMin = weeksPeriodMin.subtract(weeksPeriodMin.day(), 'days');
      } else {
        weeksPeriodMin = weeksPeriodMin.startOf('isoWeek');
      }

      return weeksPeriodMin.startOf('day').unix() * 1000;
    } else if (this.args.period === Periods.MONTHS) {
      return moment.tz(this.timezone).startOf('month').unix() * 1000;
    }

    return null;
  }

  get xAxisOptions() {
    let xAxis: XAxisOptions = {
      tickPixelInterval: 100,
    };

    if (this.xAxisMin) {
      xAxis.min = this.xAxisMin;
    }
    if (this.xAxisMax) {
      xAxis.max = this.xAxisMax;
    }

    if (this.args.xAxisDateTimePeriod === Periods.WEEKS) {
      xAxis.tickPixelInterval = 150;
      xAxis.labels = {
        style: {
          // this setting, along with "tickPixelInterval", is required for
          // "week" axis labels to not wrap
          whiteSpace: 'nowrap', // eslint-disable-line spellcheck/spell-checker
        },
      };
    }

    return xAxis;
  }

  get chartOptions(): Options {
    let yAxis = {};
    let argsYAxis = this.args.chartOptions.yAxis;

    // If the chart has multiple y axes, then yAxis will be an array. Apply
    // default settings to all of them
    if (Array.isArray(argsYAxis)) {
      yAxis = argsYAxis.map((axis) => merge({}, DEFAULT_CHART_OPTIONS.yAxis, axis));
    }

    let mergedOptions = merge(
      {},
      DEFAULT_CHART_OPTIONS,
      {
        xAxis: this.xAxisOptions,
      },
      this.args.chartOptions,
      {
        yAxis,
      },
      {
        // We put this under "chart", because otherwise highcharts doesn't
        // always seem to update custom properties. If it's nested under "chart"
        // - a property it recognizes - it will update
        chart: {
          custom: {
            xAxisDateTimePeriod: this.args.xAxisDateTimePeriod,
          },
        },
      },
      Ember.testing
        ? {
            chart: {
              animation: false,
            },
            plotOptions: {
              series: {
                animation: false,
              },
            },
            tooltip: {
              animation: false,
            },
          }
        : {},
    );

    if (mergedOptions.series) {
      mergedOptions.series = mergedOptions.series.map((s: Series) => {
        return {
          ...s,
          data: s.data.map((d) => {
            if (typeof d === 'object') {
              return {
                ...d,
                y: d.y ?? 0,
                custom: {
                  originalY: d.y,
                  isNull: d.y == null,
                  measureType: (s as any).custom?.measureType,
                  denominator: (s as any).custom?.denominator,
                },
              };
            }

            return d;
          }),
        };
      });
    }

    if (this.args.enableExport) {
      mergedOptions.exporting = {
        chartOptions: {
          chart: {
            backgroundColor: 'white',
            className: '',
          },
          legend: {
            enabled: true,
          },
          defs: null, // used to clear out gradients that aren't rendering correctly
        },
        buttons: {
          contextButton: {
            // eslint-disable-next-line spellcheck/spell-checker
            symbol: 'menuball',
            menuClassName: 'highcharts-export-menu',
            menuItems: ['downloadPNG', 'downloadPDF', 'downloadCSV'],
          },
        },
        enabled: true,
      };

      let contextButtonY = 0;
      let { legend } = mergedOptions;
      if (
        legend != null &&
        (legend.enabled === true || legend.enabled == null) &&
        legend.align === 'right' &&
        legend.verticalAlign === 'top'
      ) {
        contextButtonY = 50;
      }

      mergedOptions.navigation = {
        buttonOptions: {
          align: 'right',
          verticalAlign: 'top',
          y: contextButtonY,
        },
      };
    }

    return mergedOptions;
  }

  get height(): number {
    return Number(this.chartOptions?.chart?.height ?? DEFAULT_CHART_OPTIONS.chart.height);
  }

  get heightStyle(): SafeString {
    return htmlSafe(`height: ${this.height}px;`);
  }

  getTooltipContent(popoverClassModifier: TooltipClassModifier = TooltipClassModifier.Top) {
    let arrowOffset = popoverClassModifier === TooltipClassModifier.Top ? 8 : 10;

    return `
      <div class="fly-popover fly-popover--${popoverClassModifier} pb-0">
        <div class="fly-popover__arrow" style="bottom: -${arrowOffset}px;"></div>
        <div class="fly-popover__content p-2">
          <h3 class="mb-1.5">June 24</h3>
          <h4 class="mb-1.5">Update to calculations</h4>
          <p>
            On this date, click rate data no longer includes unsubscribe clicks
          </p>
        </div>
      </div>
    `;
  }

  @action
  displayAnnotationTooltip() {
    if (!this.chart || !this.pin) {
      return;
    }

    let { chartWidth } = this.chart;
    let tooltipClassModifier: TooltipClassModifier = TooltipClassModifier.Top;
    let pinBBox = this.pin.getBBox();
    let x = pinBBox.x + pinBBox.width / 2;
    let y = pinBBox.y - ANNOTATION_TOOLTIP_HEIGHT - 2;

    if (ANNOTATION_TOOLTIP_WIDTH / 2 + x >= chartWidth) {
      // If tooltip would render off-screen to the right
      x = x - ANNOTATION_TOOLTIP_WIDTH / 2;
      tooltipClassModifier = TooltipClassModifier.TopLeft;
    } else if (x - ANNOTATION_TOOLTIP_WIDTH / 2 <= 0) {
      // If tooltip would render off-screen to the left
      x = x + ANNOTATION_TOOLTIP_WIDTH / 2;
      tooltipClassModifier = TooltipClassModifier.TopRight;
    }

    this.pinTooltip = this.chart.renderer
      .label(
        this.getTooltipContent(tooltipClassModifier),
        x,
        y,
        undefined, // shape
        undefined, // anchorX
        undefined, // anchorY
        true, // useHTML
      )
      .css({ height: ANNOTATION_TOOLTIP_HEIGHT, width: ANNOTATION_TOOLTIP_WIDTH })
      .attr({
        align: 'center',
        zIndex: 40,
      })
      .add();
  }

  @action
  setupChartAnnotations() {
    if (
      !this.chartContainer ||
      !this.chart ||
      this.chartOptions.series == null ||
      this.chartOptions.series?.length === 0
    ) {
      return;
    }

    if (this.pin != null) {
      this.pin.destroy();
      this.pin = null;
    }
    if (this.pinHoverRect != null) {
      this.pinHoverRect.destroy();
      this.pinHoverRect = null;
    }

    let [xAxis] = this.chart.xAxis;
    let [yAxis] = this.chart.yAxis;
    let xPosition =
      (
        {
          'Etc/GMT+4': 1624492800000,
          'Etc/UTC': 1624478400000,
        } as Record<string, number>
      )[this.timezone] ?? 1624478400000;

    let xExtremes = xAxis.getExtremes();

    if (xExtremes.min > xPosition || xExtremes.max < xPosition) {
      // don't render annotation if the xAxis range doesn't contain the
      // calculation update release date
      return;
    }

    let yExtremes = yAxis.getExtremes();
    let x = xAxis.toPixels(xPosition, false) - ANNOTATION_TRIGGER_WIDTH / 2;
    let y = yAxis.toPixels(yExtremes.min, false) - ANNOTATION_TRIGGER_HEIGHT;
    this.pinHoverRect = this.chart.renderer
      .rect(
        xAxis.toPixels(xPosition, false) - ANNOTATION_HOVER_CONTAINER_SIZE / 2,
        yAxis.toPixels(yExtremes.min, false) - ANNOTATION_HOVER_CONTAINER_SIZE / 2 - ANNOTATION_TRIGGER_HEIGHT / 2,
        ANNOTATION_HOVER_CONTAINER_SIZE,
        ANNOTATION_HOVER_CONTAINER_SIZE,
      )
      .attr({ zIndex: 30, opacity: 0 })
      .add();
    this.pin = this.chart.renderer
      .image(
        new URL('cio/assets/svgs/chart-pin.svg', import.meta.url).toString(),
        x,
        y,
        ANNOTATION_TRIGGER_WIDTH,
        ANNOTATION_TRIGGER_HEIGHT,
      )
      .attr({ zIndex: 20 })
      .add();

    // eslint-disable-next-line spellcheck/spell-checker
    this.pinHoverRect?.element.addEventListener('mouseenter', this.displayAnnotationTooltip);

    // eslint-disable-next-line spellcheck/spell-checker
    this.pinHoverRect?.element.addEventListener('mouseleave', () => {
      if (this.pinTooltip != null) {
        this.pinTooltip.destroy();
        this.pinTooltip = null;
      }
    });
  }

  @action
  destroyChartAnnotations() {
    if (this.pin != null) {
      this.pin.destroy();
      this.pin = null;
    }
    if (this.pinHoverRect != null) {
      this.pinHoverRect.destroy();
      this.pinHoverRect = null;
    }
    if (this.pinTooltip != null) {
      this.pinTooltip.destroy();
      this.pinTooltip = null;
    }
  }

  willDestroy() {
    super.willDestroy();

    if (this.chart) {
      this.chart.destroy();
    }

    this.destroyChartAnnotations();
  }

  @action
  didInsert() {
    if (!this.highcharts.Highcharts) {
      (this.highcharts.loadHighcharts as any).perform();
    }
  }

  @action
  renderChart(element: HTMLElement) {
    this.chartContainer = element;
    this.chart = this.highcharts.Highcharts?.chart(this.chartContainer as HTMLDOMElement, this.chartOptions) ?? null;

    if (this.args.preserveAspectRatio && this.chart) {
      this.chart.renderer.boxWrapper.attr({
        preserveAspectRatio: this.args.preserveAspectRatio,
      });
    }

    if (this.showUntrackedChangeAnnotation) {
      this.setupChartAnnotations();
    }
  }

  @action
  rerenderChart() {
    if (this.chart) {
      this.chart.update(this.chartOptions, true, true);

      if (this.showUntrackedChangeAnnotation) {
        this.setupChartAnnotations();
      } else {
        this.destroyChartAnnotations();
      }
    }
  }
}
